import { Sparkles } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'

import {
  AiComposeModal,
  Button,
  CreateIssueModal,
  EmptyState,
  ErrorBanner,
  FilterBar,
  IssueDetailPanel,
  IssueKeyBadge,
  LoadingPane,
  Select,
  TypeIcon,
  issueLabel,
  matchesFilters,
  projectRef,
  useAiConnection,
  AiConnectPrompt,
  useDisclosure,
  useHotkey,
  useIssueFilters,
  useProjectSelection,
  useRoster,
  useSprintMutations,
  useSprints,
  useWorkspace,
  type CreateIssueDefaults,
  type Issue,
} from '@/features/issue'
import { NewProjectButton } from '@/features/project'

import { BulkActionBar } from './BulkActionBar'
import { CreateSprintModal } from './CreateSprintModal'
import { SprintSection } from './SprintSection'
import {
  BACKLOG_SECTION_KEY,
  sectionKeyFor,
  sprintIdFromSectionKey,
  useBacklogMove,
  useBacklogQuery,
  useSelection,
  type BacklogSection,
} from './useBacklog'

function sectionIndexFor(sections: BacklogSection[], id: string): number {
  const direct = sections.findIndex((section) => section.key === id)
  if (direct !== -1) return direct
  return sections.findIndex((section) => section.issues.some((issue) => issue.id === id))
}

interface Origin {
  sectionKey: string
  afterId: string | null
  beforeId: string | null
}

/**
 * The backlog planner: every open sprint, then the loose backlog.
 *
 * Two gestures do the planning. Dragging one issue moves it between sprints and
 * reorders it, with the same neighbour-based ranking the board uses. Selecting
 * a run of rows — click, ⌘-click, Shift-click — and using the action bar moves
 * them together in one transactional bulk write, which is how you actually fill
 * a sprint without dragging twenty cards one at a time.
 */
export default function BacklogPage() {
  const [params, setParams] = useSearchParams()
  const { slug, organization, userId } = useWorkspace()
  const { projectKey, project, projects, isLoading: projectsLoading } = useProjectSelection(
    organization?.id ?? null,
  )
  const { canWrite } = useRoster(slug)
  const filterController = useIssueFilters()
  const { filters } = filterController

  const includeCompleted = params.get('completed') === '1'
  const openIssueRef = params.get('issue')

  const backlog = useBacklogQuery(slug, projectKey, includeCompleted)
  const { data: sprints = [] } = useSprints(slug, projectKey, false)
  const sprintActions = useSprintMutations(slug, projectKey)
  const { move, error: moveError, clearError } = useBacklogMove(slug, projectKey, includeCompleted)

  const createIssue = useDisclosure()
  const createSprint = useDisclosure()
  const compose = useDisclosure()
  // The credential is per workspace, so this is a per-workspace question.
  // Hidden entirely when this workspace has not connected Claude: an entry
  // point that always errors is worse than no entry point. An admin gets a
  // link to the setting instead; a member gets nothing, because there is
  // nothing they could do with it.
  const ai = useAiConnection()
  const [createDefaults, setCreateDefaults] = useState<CreateIssueDefaults>({})
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null)
  const [preview, setPreview] = useState<BacklogSection[] | null>(null)
  const originRef = useRef<Origin | null>(null)

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value) next.set(key, value)
          else next.delete(key)
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const openCreate = useCallback(
    (defaults: CreateIssueDefaults = {}) => {
      setCreateDefaults({ projectKey, ...defaults })
      createIssue.open()
    },
    [createIssue, projectKey],
  )

  useHotkey('c', () => openCreate(), Boolean(canWrite && projectKey))

  /** Sections in display order, filters applied. */
  const sections = useMemo<BacklogSection[]>(() => {
    const data = backlog.data
    if (!data) return []
    const groups: BacklogSection[] = data.sprints.map((group) => ({
      key: sectionKeyFor(group.sprint?.id ?? null),
      sprint: group.sprint,
      issues: group.issues.filter((issue) => matchesFilters(issue, filters, userId)),
      count: group.count,
      points: group.points,
    }))
    groups.push({
      key: BACKLOG_SECTION_KEY,
      sprint: null,
      issues: data.backlog.issues.filter((issue) => matchesFilters(issue, filters, userId)),
      count: data.backlog.count,
      points: data.backlog.points,
    })
    return groups
  }, [backlog.data, filters, userId])

  const hiddenPerSection = useMemo(() => {
    const map = new Map<string, number>()
    const data = backlog.data
    if (!data) return map
    for (const group of data.sprints) {
      const key = sectionKeyFor(group.sprint?.id ?? null)
      const shown = sections.find((section) => section.key === key)?.issues.length ?? 0
      map.set(key, group.issues.length - shown)
    }
    const shownBacklog =
      sections.find((section) => section.key === BACKLOG_SECTION_KEY)?.issues.length ?? 0
    map.set(BACKLOG_SECTION_KEY, data.backlog.issues.length - shownBacklog)
    return map
  }, [backlog.data, sections])

  const visible = preview ?? sections

  const orderedIDs = useMemo(
    () => visible.flatMap((section) => section.issues.map((issue) => issue.id)),
    [visible],
  )
  const selection = useSelection(orderedIDs)

  const selectedIssues = useMemo(() => {
    const byID = new Map<string, Issue>()
    for (const section of visible) {
      for (const issue of section.issues) byID.set(issue.id, issue)
    }
    return [...selection.selected].map((id) => byID.get(id)).filter((issue): issue is Issue => Boolean(issue))
  }, [visible, selection.selected])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const onDragStart = (event: DragStartEvent) => {
    const issue = event.active.data.current?.issue as Issue | undefined
    if (!issue) return
    setActiveIssue(issue)
    const index = sectionIndexFor(sections, issue.id)
    const section = sections[index]
    if (section) {
      const position = section.issues.findIndex((entry) => entry.id === issue.id)
      originRef.current = {
        sectionKey: section.key,
        afterId: section.issues[position - 1]?.id ?? null,
        beforeId: section.issues[position + 1]?.id ?? null,
      }
    }
    setPreview(sections)
  }

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return
    setPreview((current) => {
      const source = current ?? sections
      const from = sectionIndexFor(source, String(active.id))
      const to = sectionIndexFor(source, String(over.id))
      if (from === -1 || to === -1 || from === to) return source

      const fromSection = source[from]!
      const toSection = source[to]!
      const moving = fromSection.issues.find((issue) => issue.id === String(active.id))
      if (!moving) return source

      const overIndex = toSection.issues.findIndex((issue) => issue.id === String(over.id))
      const insertAt = overIndex === -1 ? toSection.issues.length : overIndex

      const next = [...source]
      next[from] = {
        ...fromSection,
        issues: fromSection.issues.filter((issue) => issue.id !== moving.id),
      }
      const issues = [...toSection.issues]
      issues.splice(insertAt, 0, { ...moving, sprint_id: sprintIdFromSectionKey(toSection.key) })
      next[to] = { ...toSection, issues }
      return next
    })
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { over } = event
    const source = preview ?? sections
    const dragged = activeIssue
    setPreview(null)
    setActiveIssue(null)
    const origin = originRef.current
    originRef.current = null

    if (!over || !dragged) return

    const toIndex = sectionIndexFor(source, String(over.id))
    if (toIndex === -1) return
    const section = source[toIndex]!

    let issues = [...section.issues]
    const currentIndex = issues.findIndex((issue) => issue.id === dragged.id)
    let overIndex = issues.findIndex((issue) => issue.id === String(over.id))

    if (currentIndex === -1) {
      if (overIndex === -1) overIndex = issues.length
      issues.splice(overIndex, 0, dragged)
    } else if (overIndex !== -1 && overIndex !== currentIndex) {
      issues = arrayMove(issues, currentIndex, overIndex)
    }

    const finalIndex = issues.findIndex((issue) => issue.id === dragged.id)
    const after = issues[finalIndex - 1] ?? null
    const before = issues[finalIndex + 1] ?? null

    const unchanged =
      origin !== null &&
      origin.sectionKey === section.key &&
      origin.afterId === (after?.id ?? null) &&
      origin.beforeId === (before?.id ?? null)
    if (unchanged) return

    move({ issue: dragged, toSprintId: sprintIdFromSectionKey(section.key), after, before })
  }

  const toggleCollapsed = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const busy =
    sprintActions.start.isPending ||
    sprintActions.complete.isPending ||
    sprintActions.remove.isPending

  return (
    <div className="flex h-dvh min-h-0 flex-col">
      <header className="shrink-0 space-y-3 border-b border-ink-200 px-4 py-3 dark:border-ink-800">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-100">
            {project?.name ? `${project.name} backlog` : 'Backlog'}
          </h1>

          {projects.length > 1 && (
            <Select
              aria-label="Project"
              value={projectKey ?? ''}
              onChange={(event) => setParam('project', event.target.value)}
              className="w-44 py-1 text-xs"
            >
              {projects.map((entry) => (
                <option key={entry.id} value={projectRef(entry)}>
                  {entry.name}
                </option>
              ))}
            </Select>
          )}

          <label className="flex items-center gap-1.5 text-xs text-ink-500">
            <input
              type="checkbox"
              checked={includeCompleted}
              onChange={(event) => setParam('completed', event.target.checked ? '1' : null)}
              className="size-3.5 rounded border-ink-300 accent-brand-600"
            />
            Show completed sprints
          </label>

          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={createSprint.open} disabled={!canWrite || !projectKey}>
              New sprint
            </Button>
            {ai.connected ? (
              <Button
                size="sm"
                onClick={compose.open}
                disabled={!canWrite || !projectKey}
                title="Draft several issues from one description"
              >
                <Sparkles className="size-4" aria-hidden="true" />
                Draft with AI
              </Button>
            ) : ai.canManage && ai.settingsPath ? (
              <AiConnectPrompt to={ai.settingsPath} />
            ) : null}
            <Button
              size="sm"
              variant="primary"
              onClick={() => openCreate()}
              disabled={!canWrite || !projectKey}
              title="Create issue (c)"
            >
              Create
            </Button>
          </span>
        </div>

        <FilterBar slug={slug} controller={filterController} />
      </header>

      {moveError ? (
        <div className="px-4 pt-3">
          <ErrorBanner error={moveError} onRetry={clearError} />
        </div>
      ) : null}
      {sprintActions.start.error || sprintActions.complete.error || sprintActions.remove.error ? (
        <div className="px-4 pt-3">
          <ErrorBanner
            error={
              sprintActions.start.error ?? sprintActions.complete.error ?? sprintActions.remove.error
            }
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {backlog.isLoading || projectsLoading ? (
          <LoadingPane label="Loading backlog" />
        ) : backlog.error ? (
          <ErrorBanner error={backlog.error} onRetry={() => void backlog.refetch()} />
        ) : !projectKey ? (
          <EmptyState
            title="No project yet"
            description="A backlog is a view over one project's issues. Create a project and this fills in."
            action={<NewProjectButton variant="primary" />}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              setPreview(null)
              setActiveIssue(null)
              originRef.current = null
            }}
          >
            <div className="space-y-3">
              {visible.map((section) => (
                <SprintSection
                  key={section.key}
                  section={section}
                  selection={selection}
                  canWrite={canWrite}
                  collapsed={collapsed.has(section.key)}
                  hiddenCount={hiddenPerSection.get(section.key) ?? 0}
                  busy={busy}
                  onToggleCollapsed={() => toggleCollapsed(section.key)}
                  onOpenIssue={(ref) => setParam('issue', ref)}
                  onCreate={() => openCreate({ sprintId: section.sprint?.id ?? null })}
                  onStart={
                    section.sprint?.status === 'planning'
                      ? () => sprintActions.start.mutate(section.sprint!.id)
                      : undefined
                  }
                  onComplete={
                    section.sprint?.status === 'active'
                      ? () => {
                          if (
                            !window.confirm(
                              `Complete ${section.sprint!.name}? Unfinished issues move to the next planning sprint, or back to the backlog.`,
                            )
                          ) {
                            return
                          }
                          sprintActions.complete.mutate({ sprintId: section.sprint!.id })
                        }
                      : undefined
                  }
                  onDelete={
                    section.sprint
                      ? ((sprint) => () => {
                          const count = section.issues.length
                          // Say where the issues GO. "Delete sprint" reads like
                          // it takes the work with it, and it does not — the
                          // server returns them to the backlog in the same
                          // transaction.
                          const fate = count
                            ? `Its ${count} ${count === 1 ? 'issue moves' : 'issues move'} back to the backlog — nothing is deleted.`
                            : 'It has no issues.'
                          if (!window.confirm(`Delete ${sprint.name}? ${fate}`)) return
                          sprintActions.remove.mutate(sprint.id)
                        })(section.sprint)
                      : undefined
                  }
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeIssue ? (
                <div className="flex w-80 items-center gap-2 rounded-card border border-brand-300 bg-white px-2 py-1.5 shadow-xl dark:border-brand-700 dark:bg-ink-900">
                  <TypeIcon type={activeIssue.issue_type} />
                  <IssueKeyBadge issueKey={issueLabel(activeIssue)} />
                  <span className="truncate text-sm">{activeIssue.title}</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        <BulkActionBar
          slug={slug}
          issues={selectedIssues}
          sprints={sprints}
          onDone={selection.clear}
          onClear={selection.clear}
        />
      </div>

      <CreateIssueModal
        open={createIssue.isOpen}
        onClose={createIssue.close}
        slug={slug}
        projects={projects}
        defaults={createDefaults}
      />

      {/* Mounted while it is OPEN, not only while the workspace is connected:
          a "reconnect required" failure turns `ai.connected` false, and
          unmounting the dialog at that moment would swallow the one message
          that says what to do. Same reasoning as the board. */}
      {(ai.connected || compose.isOpen) && (
        <AiComposeModal
          open={compose.isOpen}
          onClose={compose.close}
          slug={slug}
          projects={projects}
          // No sprint: drafts made from the backlog land in the backlog, which
          // is where the planning conversation puts them.
          defaults={{ projectKey }}
        />
      )}

      <CreateSprintModal
        open={createSprint.isOpen}
        onClose={createSprint.close}
        slug={slug}
        projectKey={projectKey}
      />

      <IssueDetailPanel
        slug={slug}
        issueRef={openIssueRef}
        onClose={() => setParam('issue', null)}
        onOpenIssue={(ref) => setParam('issue', ref)}
      />
    </div>
  )
}
